import { useDispatch } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  CLIENTS_CONTACTS_ADMIN_ACCESS,
  HUBSPOT,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_CONTACTS,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useEffect, useState } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { useClientContactProfile } from "../hooks/useClientContactProfile";
import { BiLeftArrowAlt } from "react-icons/bi";
import {
  ContactProfile,
  ContactProfileToEdit,
} from "../types/clients-module-types";
import {
  dateFormatWithHours,
  hideArrowInSelectReadOnly,
} from "../../SharedModule/utils/formatters";
import { Select } from "../../SharedModule/components/Select";
import { CheckSkeleton } from "../../SharedModule/utils/checkSkeleton";
import { ClientsService } from "../services/clients";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { Calendar } from "../../SharedModule/components/Calendar";
import {
  CONTACT,
  isValidEmailFormat,
} from "../utils/clientsUtils";
import {
  setNewUpdate,
  setIsShowingActives,
} from "../redux/reducers/clientContacts.reducer";
import { restoreContactProfile } from "../redux/reducers/clientContactProfile.reducer";
import { setNeedUpdateKeyContactList } from "../redux/reducers/clientKeyContacts.reducer";
import { useModal } from "../../SharedModule/hooks/useModal";
import { useChangeStatusContact } from "../hooks/useChangeStatusContact";
import { useContactDropdownHandler } from "../hooks/useContactDropdownHandler";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientContactsProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id, contactId } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    ClientForHeaderInfo,
    isLoadingContactInfo,
    isLoadingDropdownInfo,
    actualClientId,
    contactProfile,
    userRolesList,
    rolesLoaded,
    dropdownsValues,
  } = useClientContactProfile(Number(id), Number(contactId));

  useEffect(() => {
    document.title = "The Hub - Contact Profile";

    dispatch(setActiveTab(ROUTE_CLIENTS_CONTACTS));

    return () => {
      dispatch(restoreContactProfile());
      resetScrollY(globalThis.location.pathname);
      // reset contactScrollY of contact in contact page if user got to another page
      if (
        !(
          globalThis.location.pathname.endsWith("/contacts") ||
          globalThis.location.pathname.endsWith("/contactProfile")
        )
      )
        localStorage.removeItem("contactScrollY");
    };
  }, []);

  useEffect(() => {
    if (contactProfile && dropdownsValues) {
      setInitialEmail(contactProfile.email);
      setProfile(contactProfile);

      let dateOfBirthDB = contactProfile.dateOfBirth
        ? `${contactProfile.dateOfBirth.split("T")[0]}T03:00:00.000`
        : null;
      setDateOfBirth(dateOfBirthDB ? new Date(dateOfBirthDB) : null);

      setActualCountry(
        dropdownsValues?.countries.find(
          (country) => country.value === contactProfile?.countryId
        )
      );

      if (contactProfile.stateId)
        setActualState(
          dropdownsValues?.states.find(
            (state) => state.value === contactProfile.stateId
          )
        );

      let types =
        dropdownsValues?.contactRoleTypes.filter((role) =>
          contactProfile?.contactRoleTypeId?.includes(role.value)
        ) || [];
      setActualRoleType(types);

      if (contactProfile.discProfileOptionId)
        setActualDiscProfile(
          dropdownsValues?.discProfiles.find(
            (disc) => disc.value === contactProfile.discProfileOptionId
          )
        );

      if (contactProfile.salutationId)
        setActualSalutation(
          dropdownsValues?.salutations.find(
            (salutation) => salutation.value === contactProfile.salutationId
          )
        );
    }
  }, [contactProfile, dropdownsValues]);

  let routeGoBack = ROUTE_CLIENTS_CONTACTS.replace(
    ":id",
    actualClientId ? String(actualClientId) : String(id)
  );

  const couldEditContact = verifyModuleAccess(
    true,
    CLIENTS_CONTACTS_ADMIN_ACCESS,
    userRolesList
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const [profile, setProfile] = useState<ContactProfile>(CONTACT);
  const [initialEmail, setInitialEmail] = useState(undefined);
  const [isTouched, setIsTouched] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isDuplicatedEmail, setIsDuplicatedEmail] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState<any>(null);
  const [actualCountry, setActualCountry] = useState<any>(undefined);
  const [actualState, setActualState] = useState<any>(undefined);
  const [actualRoleType, setActualRoleType] = useState<any>([]);
  const [actualDiscProfile, setActualDiscProfile] = useState<any>(undefined);
  const [actualSalutation, setActualSalutation] = useState<any>(undefined);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setIsTouched(true);

    if (name === "email") {
      setIsDuplicatedEmail(false);
      setIsValidEmail(isValidEmailFormat(value));
    }

    setProfile((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        [name]: value,
      };
    });
  };

  const handleChangeDropdown = useContactDropdownHandler(
    profile,
    setProfile,
    setIsTouched,
    {
      setActualCountry,
      setActualState,
      setActualRoleType,
      setActualDiscProfile,
      setActualSalutation,
    }
  );

  const handleBlur = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsDuplicatedEmail(false);
    const { value } = e.target;
    if (isValidEmail) {
      setIsValidatingEmail(true);
      try {
        const response = await ClientsService.getClientContactProfileByEmail(
          value
        );
        if (response.success && response.data?.email !== initialEmail) {
          dispatch(
            addToast({
              mode: "warning",
              message:
                "This email belongs to an existing contact. You can’t reuse it here — to assign it, go to 'Add New Contact'.",
            })
          );
          setIsDuplicatedEmail(true);
        }
        setIsValidatingEmail(false);
      } catch (err: any) {
        dispatch(
          addToast({
            mode: "error",
            message: "Something went wrong validating this email",
          })
        );
        setIsValidatingEmail(false);
      }
    }
  };

  const save = async () => {
    setIsLoadingSave(true);

    const {
      clients,
      contactRoleType,
      country,
      discProfile,
      lastUpdateBy,
      lastUpdateDate,
      salutation,
      state,
      ...rest
    } = profile;

    const cloned: ContactProfileToEdit = {
      ...rest,
      clientIds: clients.map((client) => client.id),
    };

    // Remove properties with null values
    const cleaned = Object.fromEntries(
      Object.entries(cloned).filter(([_, value]) => value !== null)
    );

    await ClientsService.updateContactProfile(cleaned, cloned.id)
      .then(() => {
        dispatch(
          addToast({
            mode: "success",
            message: "Contact Updated",
          })
        );
        setIsTouched(false);
        dispatch(setNewUpdate(true));
        dispatch(restoreContactProfile());
        dispatch(setNeedUpdateKeyContactList(true));
        setIsLoadingSave(false);
        navigate(routeGoBack, {
          state: {
            from:
              location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                : "",
          },
        });
      })
      .catch(() => {
        dispatch(
          addToast({
            mode: "error",
            message: "Something went wrong updating this contact",
          })
        );
        setIsLoadingSave(false);
      });
  };

  const { showModal, closeModal, Modal, ...modalProps } = useModal();

  const removeContactHook = useChangeStatusContact(
    showModal,
    closeModal,
    false,
    true,
    Number(id),
    profile
  );

  const deactivateContactHook = useChangeStatusContact(
    showModal,
    closeModal,
    false,
    false,
    Number(id),
    profile
  );

  const reactivateContactHook = useChangeStatusContact(
    showModal,
    closeModal,
    true,
    false,
    Number(id),
    profile
  );

  const removeContact = async (event: any) => {
    event.preventDefault();

    await removeContactHook().then(() => {
      setIsTouched(false);
      dispatch(setNewUpdate(true));
      dispatch(restoreContactProfile());
      dispatch(setNeedUpdateKeyContactList(true));
      navigate(routeGoBack, {
        state: {
          from:
            location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              : "",
        },
      });
    });
  };

  const deactivateContact = async (event: any) => {
    event.preventDefault();

    await deactivateContactHook().then(() => {
      setIsTouched(false);
      dispatch(setNewUpdate(true));
      dispatch(restoreContactProfile());
      dispatch(setNeedUpdateKeyContactList(true));
      dispatch(setIsShowingActives(false));
      navigate(routeGoBack, {
        state: {
          from:
            location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              : "",
        },
      });
    });
  };

  const reactivateContact = async (event: any) => {
    event.preventDefault();

    await reactivateContactHook().then(() => {
      setIsTouched(false);
      dispatch(setNewUpdate(true));
      dispatch(restoreContactProfile());
      dispatch(setNeedUpdateKeyContactList(true));
      dispatch(setIsShowingActives(true));
      navigate(routeGoBack, {
        state: {
          from:
            location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              : "",
        },
      });
    });
  };

  const isPageBusy = isLoadingContactInfo || !rolesLoaded || isLoadingSave;
  const isInputDisabled =
    !couldEditContact || !isValidEmail || isDuplicatedEmail || !isEditMode;
  const isDropdownDisabled =
    !couldEditContact ||
    isLoadingDropdownInfo ||
    !isValidEmail ||
    isDuplicatedEmail ||
    !isEditMode;
  const inputModeClass = isEditMode ? "" : "input-no-border";

  const renderActionButtons = () => (
    <>
      {ClientForHeaderInfo?.isActive &&
        couldEditContact &&
        !isEditMode &&
        profile.isActive &&
        !isLoadingSave && (
          <div>
            <button
              className="btn button-secondary"
              type="button"
              onClick={() => setIsEditMode(true)}
            >
              Edit Contact
            </button>
            {profile.clients.length > 1 && (
              <button
                className="btn button-secondary-danger ms-3"
                type="button"
                onClick={(e) => removeContact(e)}
              >
                Remove Contact
              </button>
            )}
            <button
              className="btn button-secondary-danger ms-3"
              type="button"
              onClick={(e) => deactivateContact(e)}
            >
              Deactivate Contact
            </button>
          </div>
        )}
      {ClientForHeaderInfo?.isActive &&
        couldEditContact &&
        !isEditMode &&
        !profile.isActive && (
          <button
            className="btn button-secondary ms-3"
            type="button"
            onClick={(e) => reactivateContact(e)}
          >
            Reactivate Contact
          </button>
        )}
    </>
  );

  const renderEmailField = () => (
    <div className="d-flex p-0 mb-3">
      <CheckSkeleton
        classes="flex-column w-50 me-5"
        isLoading={!profile}
        style={{ height: "38px" }}
      >
        <div className="flex-column w-50 me-5">
          <label className="col-form-label d-block">
            Email
            <span
              style={{
                color: profile?.email.length === 0 ? "red" : "black",
              }}
            >
              {" "}
              *
            </span>
            <input
              type="text"
              className={`form-control form-control-sm common-input ${inputModeClass} ${
                profile?.email.length === 0 ||
                !isValidEmail ||
                isDuplicatedEmail
                  ? "border-error"
                  : ""
              }`}
              value={profile?.email}
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!couldEditContact || isValidatingEmail || !isEditMode}
            />
          </label>
          {!isValidEmail && (
            <span className="helper-title-error">Invalid email format</span>
          )}
          {isDuplicatedEmail && (
            <span className="helper-title-error">Already existing email</span>
          )}
        </div>
      </CheckSkeleton>
      <div className="flex-column w-50 ms-5"></div>
    </div>
  );

  const renderNameFields = () => (
    <div className="d-flex p-0 mb-3">
      <CheckSkeleton
        classes="flex-column w-50 me-5"
        isLoading={!profile || isValidatingEmail}
        style={{ height: "38px" }}
      >
        <div className="flex-column w-50 me-5">
          <label className="col-form-label d-block">
            First Name
            <span
              style={{
                color: profile?.firstName.length === 0 ? "red" : "black",
              }}
            >
              {" "}
              *
            </span>
            <input
              type="text"
              className={`form-control form-control-sm common-input ${inputModeClass} ${
                profile?.firstName.length === 0 ? "border-error" : ""
              }`}
              value={profile?.firstName}
              name="firstName"
              onChange={handleChange}
              disabled={isInputDisabled}
            />
          </label>
        </div>
      </CheckSkeleton>
      <CheckSkeleton
        classes="flex-column w-50 ms-5"
        isLoading={!profile || isValidatingEmail}
        style={{ height: "38px" }}
      >
        <div className="flex-column w-50 ms-5">
          <label className="col-form-label d-block">
            Last Name
            <span
              style={{
                color: profile?.lastName.length === 0 ? "red" : "black",
              }}
            >
              {" "}
              *
            </span>
            <input
              type="text"
              className={`form-control form-control-sm common-input ${inputModeClass} ${
                profile?.lastName.length === 0 ? "border-error" : ""
              }`}
              value={profile?.lastName}
              name="lastName"
              onChange={handleChange}
              disabled={isInputDisabled}
            />
          </label>
        </div>
      </CheckSkeleton>
    </div>
  );

  const renderContactDetailsFields = () => (
    <>
      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Salutation
              <Select
                components={hideArrowInSelectReadOnly(!isEditMode)}
                className={`common-input ${inputModeClass}`}
                placeholder="Please Select"
                options={dropdownsValues?.salutations}
                value={actualSalutation}
                onChange={(salutation) =>
                  handleChangeDropdown("salutationId", salutation)
                }
                isDisabled={isDropdownDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Nickname{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.nickname}
                name="nickname"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Job Title{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.jobTitle}
                name="jobTitle"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Department{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.department}
                name="department"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Contact Role
              <span
                style={{
                  color: actualRoleType.length === 0 ? "red" : "black",
                }}
              >
                {" "}
                *
              </span>
              <Select
                components={hideArrowInSelectReadOnly(!isEditMode)}
                isMulti
                placeholder="Please Select"
                className={`common-input ${inputModeClass} ${
                  actualRoleType.length === 0 ? "select-border-error" : ""
                }`}
                options={dropdownsValues?.contactRoleTypes}
                value={actualRoleType}
                onChange={(role) =>
                  handleChangeDropdown("contactRoleTypeId", role)
                }
                isDisabled={isDropdownDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              DISC Profile
              <Select
                components={hideArrowInSelectReadOnly(!isEditMode)}
                placeholder="Please Select"
                className={`common-input ${inputModeClass}`}
                options={dropdownsValues?.discProfiles}
                value={actualDiscProfile}
                onChange={(disc) =>
                  handleChangeDropdown("discProfileOptionId", disc)
                }
                isDisabled={isDropdownDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>
    </>
  );

  const renderAddressFields = () => (
    <>
      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Street Address{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.streetAddress}
                name="streetAddress"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Street Address 2{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.streetAddress2}
                name="streetAddress2"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              City{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.city}
                name="city"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              State
              <Select
                components={hideArrowInSelectReadOnly(!isEditMode)}
                placeholder="Please Select"
                className={`common-input ${inputModeClass}`}
                options={dropdownsValues?.states}
                value={actualState}
                onChange={(state) => handleChangeDropdown("stateId", state)}
                isDisabled={isDropdownDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Postal code{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.postalCode}
                name="postalCode"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Country
              <span style={{ color: !actualCountry ? "red" : "black" }}>
                {" "}
                *
              </span>
              <Select
                components={hideArrowInSelectReadOnly(!isEditMode)}
                placeholder="Please Select"
                className={`common-input ${inputModeClass} ${
                  !actualCountry ? "select-border-error" : ""
                }`}
                options={dropdownsValues?.countries}
                value={actualCountry}
                onChange={(country) =>
                  handleChangeDropdown("countryId", country)
                }
                isDisabled={isDropdownDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>
    </>
  );

  const renderPersonalInfoFields = () => (
    <>
      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Phone Number{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.phoneNumber}
                name="phoneNumber"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Mobile Phone Number{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.mobilePhoneNumber}
                name="mobilePhoneNumber"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Date of Birth
              <Calendar
                isReadOnly={!isEditMode}
                initNull={true}
                date={dateOfBirth}
                onChange={(date: string) => {
                  setIsTouched(true);
                  setDateOfBirth(new Date(date));
                  setProfile({
                    ...profile,
                    dateOfBirth: `${date.split("T")[0]}T03:00:00.000`,
                  });
                }}
                isDisabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Hobbies{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.hobbies}
                name="hobbies"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Spouse First Name{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.spouseFirstName}
                name="spouseFirstName"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Spouse Last Name{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.spouseLastName}
                name="spouseLastName"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Assistant First Name{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.assistantFirstName}
                name="assistantFirstName"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Assistant Last Name{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.assistantLastName}
                name="assistantLastName"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Assistant Phone Number{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.assistantPhoneNumber}
                name="assistantPhoneNumber"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Assistant Email{" "}
              <input
                type="text"
                className={`form-control form-control-sm common-input ${inputModeClass}`}
                value={profile?.assistantEmail}
                name="assistantEmail"
                onChange={handleChange}
                disabled={isInputDisabled}
              />
            </label>
            {!isValidEmailFormat(profile?.assistantEmail) &&
              profile?.assistantEmail?.length > 0 && (
                <span className="helper-title-error">
                  Invalid email format
                </span>
              )}
          </div>
        </CheckSkeleton>
      </div>
    </>
  );

  const renderSaveCancelButtons = () =>
    !isLoadingSave &&
    couldEditContact &&
    isEditMode && (
      <div className="d-flex p-0 my-3 justify-content-end">
        <button
          className="btn button-secondary me-3"
          type="button"
          onClick={() => setIsEditMode(false)}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => save()}
          disabled={
            !profile ||
            !isTouched ||
            profile?.firstName.length === 0 ||
            profile?.lastName.length === 0 ||
            profile?.email.length === 0 ||
            !isValidEmail ||
            isValidatingEmail ||
            isDuplicatedEmail ||
            (!isValidEmailFormat(profile?.assistantEmail) &&
              profile?.assistantEmail?.length > 0) ||
            actualRoleType.length === 0 ||
            !actualCountry
          }
        >
          Save
        </button>
      </div>
    );

  const renderLastUpdatedInfo = () =>
    profile?.lastUpdateDate && (
      <div className="d-flex p-0 my-3">
        <span className="helper-title">
          Last updated by{" "}
          {profile?.lastUpdateBy === HUBSPOT ||
          profile?.lastUpdateBy?.length === 0
            ? HUBSPOT
            : profile?.lastUpdateBy}{" "}
          on {dateFormatWithHours(profile?.lastUpdateDate ?? "")}{" "}
          (ET) on {profile?.lastUpdateBy === HUBSPOT ? HUBSPOT : "The Hub"}
        </span>
      </div>
    );

  return (
    <>
      <Modal closeModal={closeModal} {...modalProps} />
      <div className="content">
        <HeaderClientsModule
          showBackButton={false}
          isLoadingPage={isLoadingContactInfo || !rolesLoaded}
        />

        {isPageBusy && (
          <Spinner
            style={{
              marginLeft: "50%",
              marginTop: "5%",
            }}
          />
        )}

        {!isPageBusy &&
          !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
            <ForbiddenPage />
          )}

        {!isPageBusy &&
          verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
            <>
              <Link
                className="back-button-container text-decoration-none mt-4"
                to={routeGoBack}
                state={{
                  from:
                    location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                      ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                      : "",
                }}
              >
                <BiLeftArrowAlt className="back-button-arrow" />
                <span className="back-button-text">Back to Contacts</span>
              </Link>

              {!isLoadingContactInfo && (
                <form className="card entry-hour-box-background px-4 py-2 my-3">
                  <div
                    className="d-flex justify-content-between"
                    style={{ margin: "1% 8%" }}
                  >
                    <h3 style={{ marginTop: "0.5rem" }}>
                      Contact Personal Information
                    </h3>
                    {renderActionButtons()}
                  </div>

                  <div className="row" style={{ margin: "0 8%" }}>
                    {renderEmailField()}
                    {renderNameFields()}
                    {renderContactDetailsFields()}
                    {renderAddressFields()}
                    {renderPersonalInfoFields()}
                    {renderSaveCancelButtons()}
                    {renderLastUpdatedInfo()}
                  </div>
                </form>
              )}
            </>
          )}
        <Footer />
      </div>
    </>
  );
};
